import { useState } from 'react'
import { Card, Button, Modal, Input, Message, Space } from '@arco-design/web-react'
import { useTranslation } from 'react-i18next'
import {
  setup2FAApi,
  enable2FAApi,
  disable2FAApi,
  deletePasskeyApi,
  passkeyRegisterBeginApi,
  passkeyRegisterFinishApi,
} from '@/api/user'
import { Pill } from '@/utils/render'
import {
  buildRegistrationResult,
  isPasskeySupported,
  prepareCredentialCreationOptions,
} from '@/utils/passkey'
import ChangePasswordModal from './modals/ChangePasswordModal'
import type { SecureRunner } from '@/hooks/useSecureVerification'

interface SecurityCenterCardProps {
  runSecure: SecureRunner
  twoFaEnabled: boolean
  passkeyEnabled: boolean
  onSecurityChange?: () => void
}

export default function SecurityCenterCard({
  runSecure,
  twoFaEnabled,
  passkeyEnabled,
  onSecurityChange,
}: SecurityCenterCardProps) {
  const { t } = useTranslation()
  const [passwordOpen, setPasswordOpen] = useState(false)
  const [setupOpen, setSetupOpen] = useState(false)
  const [qr, setQr] = useState('')
  const [secret, setSecret] = useState('')
  const [code, setCode] = useState('')
  const [passkeyLoading, setPasskeyLoading] = useState(false)
  const [disableCode, setDisableCode] = useState('')

  const beginSetup = async () => {
    try {
      const res = await setup2FAApi()
      setSecret(res.secret)
      setQr(res.qr_code_data || res.qr_code || '')
      setSetupOpen(true)
    } catch (err: unknown) {
      Message.error(err instanceof Error ? err.message : 'Failed')
    }
  }

  const confirmEnable = async () => {
    try {
      await enable2FAApi(code)
      Message.success(t('personal.security.twoFaEnabledMsg'))
      setSetupOpen(false)
      setCode('')
      onSecurityChange?.()
    } catch (err: unknown) {
      Message.error(err instanceof Error ? err.message : 'Failed')
    }
  }

  const handleDisable2FA = () => {
    Modal.confirm({
      title: t('personal.security.twoFaDisableTitle'),
      content: (
        <div className="space-y-2">
          <p className="text-sm text-[var(--console-text-muted)]">{t('personal.security.twoFaDisableHint')}</p>
          <Input value={disableCode} onChange={setDisableCode} placeholder="000000" />
        </div>
      ),
      onOk: async () => {
        try {
          await runSecure(() => disable2FAApi(disableCode), { scope: '2fa.disable' })
          setDisableCode('')
          Message.success(t('personal.security.twoFaDisabledMsg'))
          onSecurityChange?.()
        } catch (err: unknown) {
          if (err instanceof Error && err.message !== t('用户取消了二次验证')) {
            Message.error(err.message)
          }
        }
      },
    })
  }

  const registerPasskey = async () => {
    if (!isPasskeySupported()) {
      Message.warning(t('auth.passkeyUnsupported'))
      return
    }
    setPasskeyLoading(true)
    try {
      const begin = await passkeyRegisterBeginApi()
      const beginData = begin as { options?: unknown; publicKey?: unknown }
      const publicKey = prepareCredentialCreationOptions(beginData.options || beginData)
      const credential = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential | null
      if (!credential) throw new Error('cancelled')
      const payload = buildRegistrationResult(credential)
      await passkeyRegisterFinishApi(payload)
      Message.success(t('personal.security.passkeyRegisteredMsg'))
      onSecurityChange?.()
    } catch (err: unknown) {
      if ((err as { name?: string })?.name !== 'AbortError') {
        Message.error(err instanceof Error ? err.message : 'Failed')
      }
    } finally {
      setPasskeyLoading(false)
    }
  }

  const removePasskey = () => {
    Modal.confirm({
      title: t('personal.security.removePasskey'),
      content: t('personal.security.removePasskeyConfirm'),
      okButtonProps: { status: 'danger' },
      onOk: async () => {
        try {
          await runSecure(() => deletePasskeyApi(), { scope: 'passkey.delete' })
          Message.success(t('personal.security.passkeyRemovedMsg'))
          onSecurityChange?.()
        } catch (err: unknown) {
          if (err instanceof Error && err.message !== t('用户取消了二次验证')) {
            Message.error(err.message)
          }
        }
      },
    })
  }

  return (
    <>
      <Card className="tt-card !rounded-2xl !border-[var(--console-border)]" title={t('personal.security.title')}>
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm">{t('personal.security.changePassword')}</span>
            <Button size="small" type="outline" onClick={() => setPasswordOpen(true)}>{t('personal.security.changePassword')}</Button>
          </div>

          <div className="rounded-xl border border-[var(--console-border)] p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{t('personal.security.twoFa')}</span>
              <Pill variant={twoFaEnabled ? 'success' : 'neutral'}>
                {twoFaEnabled ? t('personal.security.twoFaEnabled') : t('personal.security.twoFaDisabled')}
              </Pill>
            </div>
            <Space>
              {twoFaEnabled ? (
                <Button size="small" status="danger" onClick={handleDisable2FA}>{t('personal.security.disableTwoFa')}</Button>
              ) : (
                <Button size="small" type="primary" onClick={() => void beginSetup()}>{t('personal.security.enableTwoFa')}</Button>
              )}
            </Space>
          </div>

          <div className="rounded-xl border border-[var(--console-border)] p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{t('personal.security.passkey')}</span>
              <Pill variant={passkeyEnabled ? 'success' : 'neutral'}>
                {passkeyEnabled ? t('personal.security.passkeyRegistered') : t('personal.security.passkeyNotRegistered')}
              </Pill>
            </div>
            <Space>
              {passkeyEnabled ? (
                <Button size="small" status="danger" onClick={removePasskey}>{t('personal.security.removePasskey')}</Button>
              ) : (
                <Button size="small" type="primary" loading={passkeyLoading} onClick={() => void registerPasskey()}>
                  {t('personal.security.registerPasskey')}
                </Button>
              )}
            </Space>
          </div>
        </div>
      </Card>

      <ChangePasswordModal visible={passwordOpen} onClose={() => setPasswordOpen(false)} runSecure={runSecure} />

      <Modal title={t('personal.security.twoFaSetupTitle')} visible={setupOpen} onCancel={() => setSetupOpen(false)} onOk={() => void confirmEnable()}>
        <p className="text-sm text-[var(--console-text-muted)] mb-3">{t('personal.security.twoFaSetupHint')}</p>
        {qr && <img src={qr} alt="2FA QR" className="mx-auto mb-3 max-w-[200px]" />}
        {secret && <p className="text-xs text-center mb-2"><code>{secret}</code></p>}
        <Input value={code} onChange={setCode} placeholder="000000" />
      </Modal>
    </>
  )
}
